import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox';
import Box from '@mui/material/Box';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FC, useLayoutEffect, useRef } from 'react';
import {
  FullscreenControl,
  MapProps,
  NavigationControl,
  PaddingOptions,
  Map as ReactMap,
  ScaleControl,
  useControl,
  useMap,
} from 'react-map-gl/mapbox';
import { envVariables } from 'src/common/utils/envVariables';

function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

type AppMapProps = {
  id: string;
  testId: string;
  initalViewState?: MapProps['initialViewState'];
  layers?: MapboxOverlayProps['layers'];
  disabled?: boolean;
  padding?: PaddingOptions;
};

export const AppMap: FC<AppMapProps> = (props) => {
  const { id, testId, initalViewState, layers, disabled, padding } = props;

  const observedElementRef = useRef<HTMLDivElement>(null);

  const maps = useMap();
  const thisMap = maps[id];

  useLayoutEffect(() => {
    if (observedElementRef.current && thisMap) {
      const observer = new ResizeObserver(() => {
        thisMap.resize();
      });

      observer.observe(observedElementRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [thisMap]);

  return (
    <Box
      data-testid={testId}
      sx={{ width: '100%', height: '100%', position: 'relative' }}
      ref={observedElementRef}
    >
      <ReactMap
        initialViewState={initalViewState}
        mapStyle="mapbox://styles/mapbox/light-v9"
        mapboxAccessToken={envVariables.VITE_MAPBOX_TOKEN}
        customAttribution="&copy; CSL Group"
        id={id}
        padding={padding}
      >
        <NavigationControl />
        <FullscreenControl />
        <ScaleControl />
        <DeckGLOverlay
          interleaved
          getCursor={(interactiveState) => {
            const { isHovering } = interactiveState;

            if (isHovering) {
              return 'pointer';
            }

            return '';
          }}
          layers={layers}
        />
      </ReactMap>
      {disabled ? (
        <Box
          data-testid={`${testId}.map-overlay-disabled`}
          sx={{
            zIndex: 2,
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            backgroundColor: '#efefefcc',
          }}
        />
      ) : null}
    </Box>
  );
};
